import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import BackButton from "../../components/common/BackButton/BackButton.tsx";
import BriaButton, { ButtonTypes } from "../../components/common/BriaButton/BriaButton";
import BriaButtonGroup from "../../components/common/BriaButtonGroup/BriaButtonGroup.tsx";
import LoadingPlaceholder from "../../components/common/LoadingPlaceholder/LoadingPlaceholder";
import styles from "./TabsLayout.module.scss";

export type ITabLayout = {
	disabled?: boolean;
	name: string;
	link: string;
	onClick?: (e: React.MouseEvent) => void;
};

interface IProps {
	children: React.ReactNode;
	title: string | ReactNode;
	subTabs: any;
	isLoading?: boolean;
	primaryButtonLabel?: string | ReactNode;
	primaryButtonClickHandler?: (event: any) => void;
	primaryButtonDisabled?: boolean;
	primaryButtonLoading?: boolean;
	secondaryButtonLabel?: string | ReactNode;
	secondaryButtonClickHandler?: (event: any) => void;
	secondaryButtonType?: ButtonTypes;
	hideTitle?: boolean;
	className?: string;
	contentClassName?: string;
	extraComponent?: React.ReactNode;
	subtitle?: React.ReactNode;
	showBackButton?: boolean;
}

const TabsLayout = ({
	children,
	title,
	subTabs,
	isLoading = false,
	primaryButtonLabel,
	primaryButtonClickHandler,
	primaryButtonDisabled = false,
	primaryButtonLoading = false,
	secondaryButtonLabel,
	secondaryButtonClickHandler,
	secondaryButtonType = "textMedium",
	hideTitle = false,
	className,
	contentClassName,
	extraComponent,
	subtitle,
	showBackButton = false,
}: IProps) => {
	const { t } = useTranslation("translation", { keyPrefix: "tabsLayout" });

	function isInstanceOfITabLayout(tab: any): tab is ITabLayout {
		return typeof tab === "object" && tab !== null && "name" in tab && "link" in tab;
	}

	return (
		<Box className={`${className} ${styles.container}`}>
			<Box className={styles.headingContainer}>
				<Box className={subtitle ? styles.headingWithSubtitle : styles.headingTitlePassThrough}>
					<Box className={styles.titleRow}>
						{showBackButton && <BackButton hideText />}
						{!hideTitle && <Typography className={styles.titleStyle}>{title}</Typography>}
					</Box>
					{subtitle}
				</Box>
				{(primaryButtonLabel || secondaryButtonLabel) && (
					<BriaButtonGroup>
						{typeof secondaryButtonLabel === "string" ? (
							<BriaButton onClick={secondaryButtonClickHandler} buttonType={secondaryButtonType}>
								{secondaryButtonLabel}
							</BriaButton>
						) : (
							secondaryButtonLabel
						)}
						{typeof primaryButtonLabel === "string" ? (
							<BriaButton
								onClick={primaryButtonClickHandler}
								buttonType="primaryMedium"
								disabled={primaryButtonDisabled}
								loading={primaryButtonLoading}
							>
								{primaryButtonLabel}
							</BriaButton>
						) : (
							primaryButtonLabel
						)}
					</BriaButtonGroup>
				)}
			</Box>

			<Box className={styles.tabsContainer}>
				{subTabs.map((tab: any, index: number) => (
					<NavLink
						key={index}
						to={isInstanceOfITabLayout(tab) ? tab.link : tab}
						className={clsx({
							[styles.tabContainer]: true,
							[styles.disabled]: isInstanceOfITabLayout(tab) ? tab.disabled : false,
						})}
						onClick={tab.onClick}
					>
						{({ isActive }: { isActive: boolean }) => (
							<>
								<h3 className={isActive ? styles.boldText : ""}>
									{isInstanceOfITabLayout(tab) ? tab.name : t(tab, { keyPrefix: false })}
								</h3>
								{isActive && <div />}
							</>
						)}
					</NavLink>
				))}
				{extraComponent && <Box className={styles.extraComponentsContainer}>{extraComponent}</Box>}
			</Box>
			<LoadingPlaceholder isLoading={isLoading} className={styles.loadingPlaceholder}>
				<Box className={`${contentClassName} ${styles.contentContainer}`}>{children}</Box>
			</LoadingPlaceholder>
		</Box>
	);
};

export default observer(TabsLayout);
